import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  CheckCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  ArrowRight,
  Plus,
  Bot
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import { HelpTooltip } from '../../components/help';
import AIRiskManagerModal from '../../components/AIRiskManagerModal';

const Dashboard = () => {
  const { user, viewMode } = useAuthStore();
  const navigate = useNavigate();
  const [firmData, setFirmData] = useState(null);
  const [showAIRiskManager, setShowAIRiskManager] = useState(false);
  const [recentRequests, setRecentRequests] = useState([]);
  const [stats, setStats] = useState([
    { name: 'Total Clients', value: '0', change: '', icon: Users, color: 'blue' },
    { name: 'Pending Requests', value: '0', change: '', icon: Clock, color: 'amber' },
    { name: 'Completed This Month', value: '0', subtitle: null, change: '', icon: CheckCircle, color: 'green' },
    { name: 'Need Review', value: '0', change: '', icon: AlertCircle, color: 'red' },
  ]);

  useEffect(() => {
    // Only fetch data if in platform_admin or company_admin view mode
    if (user && (viewMode === 'platform_admin' || viewMode === 'company_admin')) {
      fetchFirmData();
      fetchRecentRequests();
      fetchStats();
    }
  }, [user, viewMode]);

  const fetchFirmData = async () => {
    try {
      if (user?.company?.slug) {
        const response = await api.get(`/firms/${user.company.slug}`);
        setFirmData(response.data.firm);
      }
    } catch (error) {
      console.error('Failed to fetch firm data:', error);
    }
  };

  const fetchRecentRequests = async () => {
    try {
      const response = await api.get('/requests?limit=5');
      setRecentRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch recent requests:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch clients count
      const clientsRes = await api.get('/client-files?limit=1');
      const totalClients = clientsRes.data.pagination?.total || 0;

      // Fetch requests for stats
      const requestsRes = await api.get('/requests?limit=100');
      const requests = requestsRes.data.requests || [];
      const pendingCount = requests.filter(r => ['SENT', 'IN_PROGRESS'].includes(r.status)).length;
      const needReviewCount = requests.filter(r => r.status === 'NEEDS_REVIEW').length;
      const completedCount = requests.filter(r => r.status === 'APPROVED').length;

      setStats([
        { name: 'Total Clients', value: String(totalClients), change: '', icon: Users, color: 'blue' },
        { name: 'Pending Requests', value: String(pendingCount), change: '', icon: Clock, color: 'amber' },
        {
          name: 'Completed This Month',
          value: firmData?.validations_this_month || String(completedCount),
          subtitle: firmData?.monthly_validation_limit ? `out of ${firmData.monthly_validation_limit} allowed` : null,
          change: '',
          icon: CheckCircle,
          color: 'green'
        },
        { name: 'Need Review', value: String(needReviewCount), change: '', icon: AlertCircle, color: 'red' },
      ]);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-amber-100 text-amber-800',
      IN_PROGRESS: 'bg-blue-100 text-blue-800',
      SUBMITTED: 'bg-purple-100 text-purple-800',
      NEEDS_REVIEW: 'bg-orange-100 text-orange-800',
      APPROVED: 'bg-green-100 text-green-800',
      DECLINED: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user?.firstName}</p>
        </div>
      </div>

      {/* Quick Actions - Moved to top */}
      <div className={`grid gap-6 ${viewMode === 'platform_admin' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Link
          to="/requests/new"
          className="bg-blue-50 rounded-xl p-6 hover:bg-blue-100 transition-colors"
        >
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Create Request</h3>
          <p className="text-sm text-gray-600">Send a new KYC or KYB invite to a client</p>
        </Link>

        <Link
          to="/clients"
          className="bg-green-50 rounded-xl p-6 hover:bg-green-100 transition-colors"
        >
          <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mb-4">
            <Users className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">View Clients</h3>
          <p className="text-sm text-gray-600">Browse all your clients and their status</p>
        </Link>

        <Link
          to="/reports"
          className="bg-purple-50 rounded-xl p-6 hover:bg-purple-100 transition-colors"
        >
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Export Reports</h3>
          <p className="text-sm text-gray-600">Download audit packs and compliance reports</p>
        </Link>

        {viewMode === 'platform_admin' && (
          <button
            onClick={() => setShowAIRiskManager(true)}
            className="bg-indigo-50 rounded-xl p-6 hover:bg-indigo-100 transition-colors text-left"
          >
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center mb-4">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">AI Risk Manager</h3>
            <p className="text-sm text-gray-600">Analyze entities with AI assistance</p>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const colorClasses = {
            blue: 'bg-blue-50 text-blue-600',
            amber: 'bg-amber-50 text-amber-600',
            green: 'bg-green-50 text-green-600',
            red: 'bg-red-50 text-red-600',
          };

          // Help content for each stat
          const helpContent = {
            'Total Clients': 'Total number of clients who have verification requests with your company.',
            'Pending Requests': 'Verification requests waiting for client to submit documents.',
            'Completed This Month': 'Number of verifications completed this month. Usage resets on the 1st of each month.',
            'Need Review': 'Verification requests that require manual review by your team.'
          };

          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <HelpTooltip
                      content={helpContent[stat.name]}
                      size="sm"
                    />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                  )}
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[stat.color]}`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm">
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-green-600 font-medium">{stat.change}</span>
                <span className="text-gray-500 ml-2">vs last month</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Requests */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Requests</h2>
          <Link to="/requests" className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
            View all
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentRequests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No recent requests
                  </td>
                </tr>
              ) : (
                recentRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/requests/${request.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                          <span className="text-blue-600 font-medium text-sm">
                            {(request.subject_name || 'U').charAt(0)}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{request.subject_name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{request.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{request.level}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(request.status)}`}>
                        {request.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(request.created_at).toLocaleDateString('en-GB')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Risk Manager Modal */}
      <AIRiskManagerModal
        isOpen={showAIRiskManager}
        onClose={() => setShowAIRiskManager(false)}
      />
    </div>
  );
};

export default Dashboard;
